import React, { useEffect, useRef } from 'react';
import { FiX } from "react-icons/fi";

type Props = {
    children: JSX.Element;
    modalStyle: string;
    contentStyle?: string;
    closeBtnImg?: string;
    closeBtnStyle?: string;
    ariaLabel?: string;
    panelId?: string;
    onClose?: () => void;
}

const FOCUSABLE = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
].join(', ');

const Modal: React.FC<Props> = ({
    children,
    modalStyle,
    contentStyle,
    closeBtnStyle,
    ariaLabel,
    panelId,
    onClose,
}) => {
    const panelRef = useRef<HTMLDivElement>(null);

    // Callers pass inline arrows, so keep the latest without re-running the
    // effect - otherwise every re-render would re-grab focus mid-navigation.
    const onCloseRef = useRef(onClose);
    useEffect(() => {
        onCloseRef.current = onClose;
    });

    useEffect(() => {
        const { body } = document;
        const previousOverflow = body.style.overflow;
        const previouslyFocused = document.activeElement as HTMLElement | null;

        // Stops the page behind the panel scrolling on touch devices.
        body.style.overflow = 'hidden';

        const frame = requestAnimationFrame(() => {
            const panel = panelRef.current;
            if (!panel) return;

            const first = panel.querySelector<HTMLElement>(FOCUSABLE);
            (first ?? panel).focus();
        });

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                event.preventDefault();
                onCloseRef.current?.();
                return;
            }

            if (event.key !== 'Tab') return;

            const panel = panelRef.current;
            if (!panel) return;

            const focusable = Array.from(
                panel.querySelectorAll<HTMLElement>(FOCUSABLE)
            );

            if (focusable.length === 0) return;

            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            const active = document.activeElement;

            if (event.shiftKey && (active === first || active === panel)) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && active === last) {
                event.preventDefault();
                first.focus();
            }
        };

        document.addEventListener('keydown', handleKeyDown);

        return () => {
            cancelAnimationFrame(frame);
            document.removeEventListener('keydown', handleKeyDown);
            body.style.overflow = previousOverflow;
            previouslyFocused?.focus();
        };
    }, []);

    return (
        <div
            ref={panelRef}
            id={panelId}
            className={`${modalStyle} fixed`}
            role="dialog"
            aria-modal="true"
            aria-label={ariaLabel}
            tabIndex={-1}
        >
            <div className={`${contentStyle} relative z-30`}>
                {onClose === undefined ? null : (
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close menu"
                        className={`absolute right-1 top-1 z-10 w-11 h-11 flex items-center justify-center rounded-full cursor-pointer transition ease-in-out duration-250 hover:bg-Primary_Accents_2xs ${closeBtnStyle || ''}`}
                    >
                        <FiX className="w-6 h-6" />
                    </button>
                )}
                {children}
            </div>
        </div>
    );
};

export default Modal;
